import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { IS_MOBILE } from '../config';

// SeaVista — water in the world. It began as an ocean encircling the city and is
// now a POOL INSIDE THE BLOCK, on the open plot between the record store and the
// warehouse. Same shader; what changed is extent and height.
//
// Those two defaults are coupled. As an ocean the surface sat at −0.3 precisely
// so the model's ground would occlude it up to the shoreline — keep that height
// inside the block and the ground hides the pool entirely.
//
// It is opaque and depth-writing, so it doesn't "cut a hole" anywhere — the
// city's own ground simply occludes it wherever land actually exists. Where the
// model ends, water is what's already behind it.
//
// Ported from the "Seascape" raymarch (Alexander Alekseev / TDM, shadertoy
// Ms2SD1) by way of the CodePen full-screen version. TWO things had to change,
// and the second is the one that matters:
//
//  1. The original vertex shader writes clip space directly
//     (gl_Position = vec4(position, 1.0)) — a fullscreen quad that ignores the
//     camera. Here it takes the normal MVP transform so it lives in the world.
//
//  2. The original derives its ray from gl_FragCoord/resolution — the image is
//     a property of the SCREEN. Mapped onto a plane that way it is a picture of
//     the sea, not sea: flat, and sliding across the surface as you walk. Here
//     the ray is rebuilt from the REAL camera — origin at cameraPosition,
//     direction toward this fragment's world position — so perspective,
//     parallax and the horizon are all genuine. Near water shows big waves,
//     far water compresses to a haze, exactly as an ocean does.
//
// The one deliberate cheat: the ray ORIGIN keeps the original's virtual eye
// height above the waves (seaeye) instead of the camera's real height. At a
// 0.65m street eye height, true-scale waves would compress into a featureless
// strip; the virtual eye is what sets apparent wave size. Raise ?seaeye for
// bigger, closer-looking swell; lower it to flatten the water out.
//
// Cost: a raymarch, priced PER COVERED PIXEL — affordable because the water
// sits past the walk bound and holds a shallow band of screen. Phones drop the
// step/octave counts (ITERS). ?sea=0 removes it entirely.
//
// Tune live (no rebuild): ?seay (water level, relative to the city's y=0 base)
// · ?seaw (width) · ?sead (depth — set it for a rectangle instead of a square)
// · ?searot (yaw) · ?seax ?seaz (centre) · ?seay (surface height)
// · ?seaeye (wave scale) · ?seaspeed · ?sea=0 (off).
//
// NOTE ON HEIGHT: the default y = −0.3 sits BELOW the city's y=0 base so the
// model's ground occludes it — correct for an ocean showing past the shoreline,
// wrong for a pool inside the block, which the ground would simply hide. Water
// placed within the city needs a seay just above the local ground.
const qp = (k, d) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) ? v : d;
};

// Raymarch budget. The originals are 8 / 3 / 5; phones can't carry that at any
// meaningful coverage, and at this distance the detail is invisible anyway.
const ITERS = IS_MOBILE
  ? { steps: 5, geometry: 2, fragment: 3 }
  : { steps: 8, geometry: 3, fragment: 5 };

const VERT = /* glsl */`
  varying vec3 vWorld;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorld = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

const FRAG = /* glsl */`
  uniform float time;
  uniform float seaEye;        // virtual eye height above the waves — the scale knob
  uniform vec3 horizonColor;   // the world's time-of-day haze, fed from PlanetScene
  varying vec3 vWorld;

  const int NUM_STEPS = ${ITERS.steps};
  const float PI = 3.1415;

  // The original derived this from the canvas width. Ours is a fixed sampling
  // epsilon for the normal — the water has no pixel width of its own.
  const float EPSILON_NRM = 0.1 / 1024.0;

  // sea
  const int ITER_GEOMETRY = ${ITERS.geometry};
  const int ITER_FRAGMENT = ${ITERS.fragment};
  const float SEA_HEIGHT = 0.6;
  const float SEA_CHOPPY = 2.0;
  const float SEA_SPEED = 0.8;
  const float SEA_FREQ = 0.16;
  const vec3 SEA_BASE = vec3(0.1, 0.19, 0.22);
  const vec3 SEA_WATER_COLOR = vec3(0.8, 0.9, 0.6);
  const float SKY_INTENSITY = 1.0;

  #define SEA_TIME time * SEA_SPEED

  float hash(vec2 p) {
    float h = dot(p, vec2(127.1, 311.7));
    return fract(sin(h) * 43758.5453123);
  }
  float noise(in vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return -1.0 + 2.0 * mix(mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
                            mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
  }

  // lighting
  float diffuse(vec3 n, vec3 l, float p) { return pow(dot(n, l) * 0.4 + 0.6, p); }
  float specular(vec3 n, vec3 l, vec3 e, float s) {
    float nrm = (s + 8.0) / (PI * 8.0);
    return pow(max(dot(reflect(e, n), l), 0.0), s) * nrm;
  }

  // sky — used for reflections and for the haze where the water meets distance
  vec3 sky_color(vec3 e) {
    e.y = max(e.y, 0.0);
    vec3 ret;
    ret.x = pow(1.0 - e.y, 2.0);
    ret.y = 1.0 - e.y;
    ret.z = 0.6 + (1.0 - e.y) * 0.4;
    return ret * SKY_INTENSITY;
  }

  // sea
  float sea_octave(vec2 uv, float choppy) {
    uv += noise(uv);
    vec2 wv = 1.0 - abs(sin(uv));
    vec2 swv = abs(cos(uv));
    wv = mix(wv, swv, wv);
    return pow(1.0 - pow(wv.x * wv.y, 0.65), choppy);
  }

  float map(vec3 p) {
    float freq = SEA_FREQ;
    float amp = SEA_HEIGHT;
    float choppy = SEA_CHOPPY;
    vec2 uv = p.xz; uv.x *= 0.75;
    mat2 m = mat2(1.6, 1.2, -1.2, 1.6);
    float d, h = 0.0;
    for (int i = 0; i < ITER_GEOMETRY; i++) {
      d = sea_octave((uv + SEA_TIME) * freq, choppy);
      d += sea_octave((uv - SEA_TIME) * freq, choppy);
      h += d * amp;
      uv *= m; freq *= 1.9; amp *= 0.22;
      choppy = mix(choppy, 1.0, 0.2);
    }
    return p.y - h;
  }
  float map_detailed(vec3 p) {
    float freq = SEA_FREQ;
    float amp = SEA_HEIGHT;
    float choppy = SEA_CHOPPY;
    vec2 uv = p.xz; uv.x *= 0.75;
    mat2 m = mat2(1.6, 1.2, -1.2, 1.6);
    float d, h = 0.0;
    for (int i = 0; i < ITER_FRAGMENT; i++) {
      d = sea_octave((uv + SEA_TIME) * freq, choppy);
      d += sea_octave((uv - SEA_TIME) * freq, choppy);
      h += d * amp;
      uv *= m; freq *= 1.9; amp *= 0.22;
      choppy = mix(choppy, 1.0, 0.2);
    }
    return p.y - h;
  }

  vec3 sea_color(in vec3 p, in vec3 n, in vec3 eye, in vec3 dist) {
    float fresnel_o = 1.0 - max(dot(n, -eye), 0.0);
    float fresnel = pow(fresnel_o, 3.0) * 0.65;
    vec3 refl = sky_color(reflect(eye, n));
    vec3 ret = SEA_BASE;
    ret = mix(ret, refl, fresnel);
    float atten = max(1.0 - dot(dist, dist) * 0.001, 0.0);
    ret += SEA_WATER_COLOR * (p.y - SEA_HEIGHT) * 0.18 * atten;
    return ret;
  }

  // tracing
  vec3 getNormal(vec3 p, float eps) {
    vec3 n;
    n.y = map_detailed(p);
    n.x = map_detailed(vec3(p.x + eps, p.y, p.z)) - n.y;
    n.z = map_detailed(vec3(p.x, p.y, p.z + eps)) - n.y;
    n.y = eps;
    return normalize(n);
  }
  float hftracing(vec3 ori, vec3 dir, out vec3 p) {
    float tm = 0.0;
    float tx = 1000.0;
    float hx = map(ori + dir * tx);
    if (hx > 0.0) { p = ori + dir * tx; return tx; }
    float hm = map(ori + dir * tm);
    float tmid = 0.0;
    for (int i = 0; i < NUM_STEPS; i++) {
      tmid = mix(tm, tx, hm / (hm - hx));
      p = ori + dir * tmid;
      float hmid = map(p);
      if (hmid < 0.0) { tx = tmid; hx = hmid; }
      else { tm = tmid; hm = hmid; }
    }
    return tmid;
  }

  void main() {
    // THE RAY IS THE REAL ONE: from the camera, through this patch of water.
    // Everything downstream is the original shader; this is what turns a
    // picture of the sea into sea.
    vec3 dir = normalize(vWorld - cameraPosition);

    // A ray that isn't heading downward can't be looking at water — only
    // grazing/behind-camera edge fragments do that. The threshold is tiny on
    // purpose: at 2km out and ~1m of eye height, legitimate far water has a
    // dir.y around -0.0006, so a coarser cut-off would slice the horizon off.
    if (dir.y > -1e-6) discard;

    // Virtual eye above the waves (see the header): x/z stay at the origin and
    // drift with time, so the swell rolls without the whole field sliding when
    // the camera moves.
    vec3 ori = vec3(0.0, seaEye, time * 0.05);

    vec3 p;
    hftracing(ori, dir, p);
    vec3 dist = p - ori;
    vec3 n = getNormal(p, dot(dist, dist) * EPSILON_NRM);

    vec3 color = sea_color(p, n, dir, dist);
    vec3 light = normalize(vec3(0.0, 1.0, 0.8));
    color += vec3(diffuse(n, light, 80.0) * SEA_WATER_COLOR) * 0.12;
    color += vec3(specular(n, light, dir, 60.0));

    // Near-horizon rays blend into the WORLD's haze (not the shader's own stock
    // blue) — that's what stops the far edge reading as a hard line against the
    // skyline ring, and keeps the water in the same air as everything else as
    // the day turns. sky_color still drives the wave reflections above.
    color = mix(horizonColor, color, pow(smoothstep(0.0, -0.05, dir.y), 0.3));
    color = pow(color, vec3(0.75));
    gl_FragColor = vec4(color, 1.0);
  }
`;

export default function SeaVista({ horizon = '#9ec3d8', position = null, quaternion = null, size = null }) {
  const mat = useRef();

  // Centred on the origin, because the city is: CityModel fits its larger
  // horizontal span to FIT_METRES (240) centred on 0 with its base dropped to
  // y=0. So the shoreline is wherever the model's ground actually ends.
  //
  // y = −0.3 is deliberate: it is exactly where CitySkyline's terrain disc used
  // to sit. This REPLACES that disc (PlanetScene passes ground={!SEA}) rather
  // than hiding under it — an opaque surface at −0.3 beats one at −0.6 every
  // time, which is why the sea was only visible past the disc's 570m edge.
  // Still below the city's y=0 base, so the model's own ground occludes it.
  // Standalone defaults describe the OCEAN (mounted by PlanetScene, sized past
  // the skyline ring). A pool inside the city comes through as props from a
  // 'water' placement instead — placed and sized with the tool that raycasts the
  // real ground, rather than as coordinates guessed into this file.
  const w = size ? size[0] : qp('seaw', 4000);
  // Depth defaults to the width, so leaving it alone keeps the original square
  // that encircles the city. Set it and the water becomes a RECTANGLE — which
  // is what a plot between two buildings needs, since a square either falls
  // short of the far end or swallows the neighbours.
  const d = size ? size[1] : qp('sead', w);
  // Yaw, for aligning the pool to the street grid rather than to world axes.
  // The mesh is laid flat by the −π/2 X rotation; the third Euler term then
  // spins it about world Y.
  const rot = qp('searot', 0);
  // −0.3 is right for the OCEAN: below the city's y=0 base, so the model's own
  // ground occludes it up to the shoreline. A placement supplies its own y,
  // raycast onto the actual ground it sits on.
  const waterY = qp('seay', -0.3);
  const seaEye = qp('seaeye', 3.5); // the original's eye height = wave scale
  const speed = qp('seaspeed', 1);

  const uniforms = useMemo(() => ({
    time: { value: 0 },
    seaEye: { value: seaEye },
    horizonColor: { value: new THREE.Color(horizon) },
  }), [seaEye]); // eslint-disable-line react-hooks/exhaustive-deps

  // Time of day moves the world's haze; follow it rather than rebuilding the
  // material, so the water's far edge always melts into the same air the
  // skyline does.
  useFrame((_, dt) => {
    if (!mat.current) return;
    mat.current.uniforms.time.value += dt * speed;
    mat.current.uniforms.horizonColor.value.set(horizon);
  });

  return (
    <mesh
      position={position || [qp('seax', 0), waterY, qp('seaz', 0)]}
      {...(quaternion
        ? { quaternion }
        : { rotation: [-Math.PI / 2, 0, rot] })}
    >
      <planeGeometry args={[w, d]} />
      {/* No fog chunks in this shader, so it is inherently unfogged — which is
          what we want: scene fog ends at 260 and would otherwise erase water
          this far out (same reason CitySkyline disables fog on its materials).
          Opaque and depth-writing, so the city's own ground correctly hides the
          water wherever the land actually is. */}
      <shaderMaterial
        ref={mat}
        args={[{ uniforms, vertexShader: VERT, fragmentShader: FRAG }]}
        side={THREE.DoubleSide}
        toneMapped={false}
      />
    </mesh>
  );
}
